/**
 * The set of external-IdP account-linking providers deSjeff offers, and the
 * per-deploy feature flag for each (an empty idpId ⇒ off — see config.ts).
 *
 * A new provider is added here + a new `VITE_<X>_IDP_ID` build var — nothing
 * else in the linking machinery (idpLink.ts, IdpLinkControl, the callback
 * route) needs to change.
 */

import { config } from "../config";

export type LinkProviderKey = "google" | "facebook";

export interface LinkProviderConfig {
  key: LinkProviderKey;
  idpId: string;
  /** Button label, e.g. "Koble til Google". */
  connectLabel: string;
  /** Bare provider name used in status copy, e.g. "Google tilkoblet". */
  displayName: string;
}

const PROVIDER_COPY: Record<LinkProviderKey, { connectLabel: string; displayName: string }> = {
  google: { connectLabel: "Koble til Google", displayName: "Google" },
  facebook: { connectLabel: "Koble til Facebook", displayName: "Facebook" },
};

function idpIdFor(key: LinkProviderKey): string {
  switch (key) {
    case "google":
      return config.zitadel.googleIdpId;
    case "facebook":
      return config.zitadel.facebookIdpId;
  }
}

/** Every provider this deploy has configured (non-empty idpId), in a stable order. */
export function getConfiguredLinkProviders(): LinkProviderConfig[] {
  const keys: LinkProviderKey[] = ["google", "facebook"];
  return keys
    .map((key) => ({ key, idpId: idpIdFor(key), ...PROVIDER_COPY[key] }))
    .filter((provider) => provider.idpId !== "");
}

/**
 * Resolve a provider by its URL-path key (the `:provider` route param), only
 * among providers this deploy has actually configured. Returns `undefined`
 * for an unknown or unconfigured key — callers must treat that as a plain
 * "feature not available" error, never a crash.
 */
export function getConfiguredLinkProvider(key: string): LinkProviderConfig | undefined {
  return getConfiguredLinkProviders().find((provider) => provider.key === key);
}
